import { Mutable } from '../global/global-interfaces';

const JSONbig = require('json-bigint');
import { Resolution } from './api-resolution';
import { NavigationService } from '@ngx-seatback/utils';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { SbDateTime, SbLocalDate, SbLocalDateTime, SbLocalTime } from './api-date-time';
import { AppEnvironment } from 'src/app/app.environment';
import { RoutingID } from '../../routes/sb-routing-enum';
import { AuthenticationService } from '../authentication.service';
import { ErrorCode } from './errorCodes';
import { AiAPI, Pagination, Result } from './aiApi';
import { AiAuthenticationService } from '../aiAuthentication.service';

@Injectable()
export class AiAPIService {
  _url: string = '/ai';

  set url(url: string) {
    this._url = url;
    this.authService.url = url;
  }

  reqHeader = new HttpHeaders();
  api: AiAPI = null;
  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  forbiddenError: any;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly navService: NavigationService,
    private readonly authService: AiAuthenticationService
  ) {}

  logout(pressed?: boolean) {
    this.destroySubs();
    this.authService.logout();
    this.destroySubs();
    if (!pressed) {
      if (
        this.router.url.indexOf('admin') === -1 &&
        this.router.url.indexOf('login') === -1 &&
        this.router.url.indexOf('passwordReset') === -1
      ) {
        this.navService.navigateToPoint(RoutingID.login, [], {
          queryParams: {
            redirect: window.location.href.replace(window.location.origin, ''),
          },
        });
      }
    } else {
      this.navService.navigateToPoint(RoutingID.login, null, {
        queryParams: {
          loggedOut: true,
        },
      });
    }
  }

  public destroySubs(): void {
    // This aborts all HTTP requests.
    this.ngUnsubscribe.next(null);
    // This completes the subject properly.
    this.ngUnsubscribe.complete();
  }

  paginate(url: string, pagination: Pagination): string {
    if (!pagination) {
      return url;
    }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'page=' + pagination.page + '&count=' + pagination.count;
  }

  buildUrl(url: string): string {
    if (url) {
      return this._url + url;
    }
  }

  public GET(url: string, paginationBehaviourSubject: BehaviorSubject<Pagination> = null): Observable<any> {
    if (paginationBehaviourSubject) {
      return this.unwrap(
        this.http.get(this.paginate(this.buildUrl(url), paginationBehaviourSubject.getValue()), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        paginationBehaviourSubject
      );
    } else {
      return this.unwrap(
        this.http.get(this.urlPrepare(url), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        null
      );
    }
  }

  public POST(url: string, body?: any, paginationBehaviourSubject: BehaviorSubject<Pagination> = null): Observable<any> {
    if (AppEnvironment.SCREENSHOT_MODE) {
      return;
    }
    if (paginationBehaviourSubject) {
      return this.unwrap(
        this.http.post(this.paginate(this.buildUrl(url), paginationBehaviourSubject.getValue()), this.bodyPrepare(body), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        paginationBehaviourSubject
      );
    } else {
      return this.unwrap(
        this.http.post(this.urlPrepare(url), this.bodyPrepare(body), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        null
      );
    }
  }

  public PUT(url: string, body?: any, paginationBehaviourSubject: BehaviorSubject<Pagination> = null): Observable<any> {
    if (AppEnvironment.SCREENSHOT_MODE) {
      return;
    }
    if (paginationBehaviourSubject) {
      return this.unwrap(
        this.http.put(this.paginate(this.buildUrl(url), paginationBehaviourSubject.getValue()), this.bodyPrepare(body), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        paginationBehaviourSubject
      );
    } else {
      return this.unwrap(
        this.http.put(this.urlPrepare(url), this.bodyPrepare(body), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        null
      );
    }
  }

  public DELETE(url: string, body?: any, paginationBehaviourSubject: BehaviorSubject<Pagination> = null): Observable<any> {
    if (AppEnvironment.SCREENSHOT_MODE) {
      return;
    }
    if (paginationBehaviourSubject) {
      return this.unwrap(
        this.http.delete(this.paginate(this.buildUrl(url), paginationBehaviourSubject.getValue()), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        paginationBehaviourSubject
      );
    } else {
      return this.unwrap(
        this.http.delete(this.urlPrepare(url), {
          headers: this.reqHeader,
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
        null
      );
    }
  }

  public queryParamArrayPrepare(name: string, arr: any[]): string {
    if (!arr || !name) {
      return '';
    }
    let retStr: string = '';

    for (const e of arr) {
      if (e) {
        retStr += '&' + name + '=';
        if (typeof e !== 'string') {
          retStr += encodeURIComponent(JSONbig.stringify(e));
        } else {
          retStr += encodeURIComponent(e);
        }
      }
    }

    return retStr;
  }

  public filterParamArray(params: { by: any; param?: string; values: string[] }[]): string[] {
    const arr: string[] = [];
    for (const param of params) {
      arr.push(param.by + (param.param ? '(' + param.param + ')' : '') + ' ' + JSON.stringify(param.values).trim());
    }
    return arr;
  }

  queryParamResolutionPrepare(resolutions: Resolution[]): string[] {
    if (!resolutions || resolutions.length === 0 || !(resolutions instanceof Array)) {
      return null;
    }
    return resolutions.map((resolution: Resolution) => encodeURI(resolution.width + 'x' + resolution.height));
  }

  public queryParamDatePrepare(dates: any): any {
    if (!dates) {
      return null;
    }
    if (dates instanceof Array) {
      const ret = [];
      for (const element of dates) {
        if (!element) {
          ret.push(null);
        } else {
          ret.push(this.queryParamDatePrepare(element));
        }
      }
      return ret;
    } else if (dates.isLocalDateTime) {
      // Check if function exists
      if (dates.isLocalDateTime()) {
        return encodeURI((dates as SbLocalDateTime).toString()).replace('+', encodeURIComponent('+'));
      } else {
        return encodeURI((dates as SbDateTime).toString()).replace('+', encodeURIComponent('+'));
      }
    } else if (dates.isSbApiTime) {
      // Check if function exists
      return encodeURI((dates as SbLocalTime).toString());
    }
    return dates.isLocalDate && dates.isLocalDate() ? dates.toString() : dates;
  }

  public asString(pAny: any): string {
    return encodeURI(JSON.stringify(pAny));
  }

  private unwrap(content: Observable<any>, pagination: BehaviorSubject<Pagination> = null): Observable<any> {
    return content.pipe(
      map((httpResponse: HttpResponse<Result>) => {
        let data = httpResponse.body;
        try {
          data = JSONbig.parse(data);
        } catch (e) {
          console.log(e);
          return data;
        }
        let prepared = null;
        if (data) {
          if (pagination && data.pagination) {
            pagination.next(data.pagination);
          }
          prepared = this.objectPrepare(data.data);
        }
        return prepared;
      }),
      catchError((data: Mutable<HttpErrorResponse>, caught) => {
        // debugger;
        data.error = JSONbig.parse(data.error);
        if (data.status === 400 && data.error.errorCode === ErrorCode.INVALID_JWT_TOKEN) {
          this.logout();
          this.navService.navigateToPoint(RoutingID.login);
        } else if (data?.status === 403) {
          this.forbiddenError = data;
          this.navService.navigateToPoint(RoutingID.error, [], { queryParams: { error: data?.status } });
          return of({});
        } else {
          if (data.error?.text) {
            return of(data.error.text);
          }
          throw data;
        }
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  private objectPrepare(prepared) {
    return this.prepareObject(prepared);
  }

  private prepareObject(prepared) {
    if (prepared) {
      if (Array.isArray(prepared)) {
        for (const key in prepared) {
          if (key) {
            prepared[key] = this.singleObjectPrepare(prepared[key]);
          }
        }
      } else {
        prepared = this.singleObjectPrepare(prepared);
      }
    }
    return prepared;
  }

  private singleObjectPrepare<T>(prepared) {
    if (prepared == null) {
      return prepared;
    }
    if (typeof prepared === 'boolean') {
      return prepared;
    }
    if (prepared._type) {
      const dto = this.api.createDTO(prepared._type);
      if (dto) {
        this.api.copy(prepared, dto);
        prepared = dto;
      }
      for (const field in prepared) {
        if (field) {
          prepared[field] = this.prepareObject(prepared[field]);
        }
      }
    } else if (
      prepared._isBigNumber && // JSONBig: Go die and good bye!
      prepared.toNumber /*check if fn exists*/ &&
      prepared.isInteger /*check if fn exists*/ &&
      !prepared.isInteger() /* check fn return value*/ // We only need BIG numbers for IDs, which are Integers.
    ) {
      prepared = prepared.toNumber(); // Return number value if BigNumber is not an Integer.
    } else if (typeof prepared === 'object') {
      for (const field in prepared) {
        if (field) {
          prepared[field] = this.prepareObject(prepared[field]);
        }
      }
    } else if (typeof prepared === 'string') {
      // tslint:disable-next-line:max-line-length
      if (
        /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?((Z)|((\+|\-)[0-9][0-9]:[0-9][0-9]))?$/.test(
          prepared
        )
      ) {
        const dt = DateTime.fromISO(prepared);
        let isLocal = true;
        if (/[+-][0-9]{2}:[0-9]{2}\b|(Z)$/.test(prepared)) {
          isLocal = false;
        }
        if (isLocal) {
          prepared = new SbLocalDateTime();
        } else {
          prepared = new SbDateTime();
        }
        prepared.value = dt;
        return prepared;
      }
      if (/^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])?$/.test(prepared)) {
        const dt = DateTime.fromISO(prepared);
        prepared = new SbLocalDate();

        prepared.value = dt;
        return prepared;
      }
      if (/^(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?((Z)|((\+|\-)[0-9][0-9]:[0-9][0-9]))?$/.test(prepared)) {
        prepared = SbLocalTime.fromIso(prepared);
        return prepared;
      }

      if (AppEnvironment.SCREENSHOT_MODE) {
        prepared = AppEnvironment.ANONYMIZE(prepared);
        return prepared;
      }
    }
    return prepared;
  }

  private bodyPrepare(bdy: any) {
    if (bdy instanceof FormData) {
      return bdy;
    }

    if (typeof bdy === 'string') {
      return bdy;
    }

    let body;

    if (Array.isArray(bdy)) {
      body = [];
    } else {
      body = {};
    }
    // array must be iterated too
    // const body: any = {};
    Object.assign(body, bdy);
    for (const k in body) {
      if (body[k]?.isLocalDateTime) {
        // Check if function exists
        if (body[k].isLocalDateTime()) {
          body[k] = encodeURI((body[k] as SbLocalDateTime).toString());
        } else {
          body[k] = encodeURI((body[k] as SbDateTime).toString());
        }
      } else if (body[k]?.isSbApiTime) {
        // Check if function exists
        body[k] = encodeURI((body[k] as SbLocalTime).toString());
      } else if (body[k]?.isLocalDate) {
        // Check if function exists
        body[k] = encodeURI((body[k] as SbLocalDate).toString());
      } else if (typeof body[k] === 'object' && body[k]) {
        body[k] = this.bodyPrepare(body[k]);
      }
    }
    return body;
  }

  private urlPrepare(url: string): string {
    if (url.startsWith('http')) {
      return url;
    }
    return this.buildUrl(url);
  }

  asMultipart(def: MultipartDefinition): any {
    const formData: FormData = new FormData();
    for (const element of def.partDefinitions()) {
      formData.set(element.name, element.field);
    }
    return formData;
  }
}

export abstract class MultipartDefinition {
  abstract partDefinitions(): {
    name: string;
    field: File;
  }[];
}
